import React from 'react';
import { useTranslation } from 'react-i18next';
import { MdCheckBox, MdRadioButtonChecked, MdCheckBoxOutlineBlank, MdRadioButtonUnchecked } from 'react-icons/md';
import { IoMdCheckmark } from 'react-icons/io';
import { IoClose } from 'react-icons/io5';
import { Box, Stack, HStack, Tooltip, Icon, Text } from '@chakra-ui/core';
import { QuestionOnReviewT, QuestionType } from './ReviewQuestion.types';
import { MultipleChoiceQuestionResponse } from '../../../../firebase/firestore/documents/candidate';
import { CorrectAnswerDeterminationType } from '../../positionManagement/PositionType';
import { ImagePreviewModal } from '../../../../app/components/ImagePreviewModal';
import ImageView from '../../../../app/components/ImageView';
import colors from '../../../../styles/colors';

export const ReviewMCQ = React.memo(function ReviewMCQ({ question }: { question: QuestionOnReviewT }) {
  const { t } = useTranslation();
  if (question.type !== QuestionType.MCQ) return null;

  const isMultiSelect = question.totalAnswer === 'any' || (question.preferredAnswers?.length ?? 0) > 1;

  const getAnswerConfig = (option: MultipleChoiceQuestionResponse['options'][0]) => {
    const hasAnswered = option.wasAnswered;
    const isPreferred = question.preferredAnswers?.includes(option.id);
    const isCorrect = hasAnswered && isPreferred;
    const isIncorrect = hasAnswered && !isPreferred;
    const isUnselectedCorrect = !hasAnswered && isPreferred;

    const SelectedIcon = isMultiSelect ? MdCheckBox : MdRadioButtonChecked;
    const UnselectedIcon = isMultiSelect ? MdCheckBoxOutlineBlank : MdRadioButtonUnchecked;

    if (isCorrect) {
      return {
        icon: SelectedIcon,
        iconColor: 'blue',
        iconOpacity: 1,
        textProps: {},
        labelIcon: IoMdCheckmark,
        testId: 'correct',
        tooltip: t('toReviewQuestions.message.selectedCorrectAnswer'),
      };
    }
    if (isIncorrect) {
      return {
        icon: SelectedIcon,
        iconColor: 'blue',
        iconOpacity: 1,
        textProps: {},
        labelIcon: IoClose,
        testId: 'wrong',
        tooltip: t('toReviewQuestions.message.selectedIncorrectAnswer'),
      };
    }
    if (isUnselectedCorrect) {
      return {
        icon: UnselectedIcon,
        iconColor: undefined,
        iconOpacity: 0.5,
        textProps: {},
        labelIcon: IoMdCheckmark,
        testId: 'wrong',
        tooltip: t('toReviewQuestions.message.correctAnswer'),
      };
    }
    return {
      icon: UnselectedIcon,
      iconColor: undefined,
      iconOpacity: 0.5,
      textProps: {},
      testId: 'wrong',
    };
  };

  const getCorrectAnswerDeterminationText = (): string => {
    const { totalAnswer } = question;
    const type = question.correctAnswerDeterminationType;
    if (!totalAnswer || !type) return '';
    switch (type) {
      case CorrectAnswerDeterminationType.IS_EQUAL: {
        if (totalAnswer === 'any') {
          return t('toReviewQuestions.mcq.correctAnswerDetermination.isEqual.anyOptions');
        }

        return t('toReviewQuestions.mcq.correctAnswerDetermination.isEqual.exactCount', {
          count: totalAnswer,
        });
      }

      case CorrectAnswerDeterminationType.HAS_ANSWER: {
        if (totalAnswer === 'any') {
          return t('toReviewQuestions.mcq.correctAnswerDetermination.hasAnswer.anyOptions');
        }

        return t('toReviewQuestions.mcq.correctAnswerDetermination.hasAnswer.atLeastCount', {
          count: totalAnswer,
        });
      }

      case CorrectAnswerDeterminationType.DOES_NOT_HAVE_ANSWER: {
        if (totalAnswer === 'any') {
          return t('toReviewQuestions.mcq.correctAnswerDetermination.doesNotHaveAnswer.anyOptions');
        }

        return t('toReviewQuestions.mcq.correctAnswerDetermination.doesNotHaveAnswer.excludeOptions', {
          count: totalAnswer,
        });
      }

      case CorrectAnswerDeterminationType.AT_LEAST: {
        if (totalAnswer === 'any') {
          return t('toReviewQuestions.mcq.correctAnswerDetermination.atLeast.anyOptions');
        }

        return t('toReviewQuestions.mcq.correctAnswerDetermination.atLeast.includeCount', {
          count: totalAnswer,
        });
      }

      // istanbul ignore next
      default:
        return '';
    }
  };

  return (
    <Box>
      {question.images && question.images.length > 0 && (
        <Box mb={2}>
          <ImagePreviewModal
            images={question.images}
            getImageKey={(image) => image?.galleryId || /* istanbul ignore next */ ''}
            getImageSrc={(image) => image?.url || /* istanbul ignore next */ ''}
            setTrigger={<ImageView height={100} src={question.images[0]?.url} />}
          />
        </Box>
      )}

      <Text as="span" fontSize="xs" display="flex" alignItems="center" fontWeight="500" fontStyle="italic" mb={2}>
        {getCorrectAnswerDeterminationText()}
      </Text>

      <Stack spacing={2} direction="column">
        {question.options?.map((option) => {
          const answerConfig = getAnswerConfig(option);
          const MCQIcon = answerConfig.icon;

          return (
            <HStack flex={1} key={option.id}>
              <Box flexShrink={0} pt="1">
                <MCQIcon
                  fontSize="sm"
                  color={answerConfig.iconColor}
                  opacity={answerConfig.iconOpacity}
                  data-testid={`answer-${answerConfig.testId}-${option.id}`}
                />
              </Box>
              <Text fontSize="sm" {...answerConfig.textProps} color={colors.text.light}>
                {option.text}
              </Text>
              {answerConfig.labelIcon && (
                <HStack spacing={1}>
                  <Tooltip
                    label={answerConfig?.tooltip}
                    hasArrow
                    bg="black"
                    size="sm"
                    borderRadius="md"
                    fontSize="xs"
                    fontWeight="bold"
                  >
                    <Box>
                      <Icon as={answerConfig.labelIcon} fontSize="xs" />
                    </Box>
                  </Tooltip>
                </HStack>
              )}
            </HStack>
          );
        })}
      </Stack>
    </Box>
  );
});
